/* eslint-disable no-nested-ternary */
import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { suggestPackages } from '@vbee-holding/aivoice-shared-lib';
import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/styles';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import {
  CURRENCY_UNIT,
  DURATION,
  PACK_DURATION,
  PACKAGE_EXPIRED,
  PACKAGE_TYPE,
} from '@src/constants/package';
import {
  filterPackagesV3,
  checkHasDiscountPackage,
  getPackageCurrentPrice,
  getTotalPricePackage,
  calculateSaleOffMonthlyPrice,
  roundSaleOffPercentage,
} from '@src/services/package';
import moment from 'moment';
import {
  checkVoucherPriceOrPercent,
  getOptimalVouchersForPackage,
} from '@src/services/voucher';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import useIpLocation from '@src/hooks/useLocation';
import Typography from '@src/new-components/Typography';
import { COLOR } from '@src/styles/color';
import { checkVietNam } from '@src/utils/checkCountry';
import BestPackItem from './PackageItem';
import NewBestPackageItem from './NewPackageItem';
import { StyledPackage } from './index.style';
import TabDuration from '../../TabDuration';

const BestPackageSuggestion = ({
  packagesData,
  allVouchers,
  useVoiceClone,
  valueSlider,
  options,
}) => {
  const theme = useTheme();
  const isVietNam = checkVietNam();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { getFeatureValue } = useFeatureFlags();
  const { ip } = useIpLocation();

  const packageType = PACKAGE_TYPE.STUDIO;

  const [packageDuration, setPackageDuration] = useState(DURATION.YEARLY);
  const [packages, setPackages] = useState([]);
  const [packagesDraft, setPackagesDraft] = useState([]);
  const [totalVoucher, setTotalVoucher] = useState([]);
  const [currentPackages, setCurrentPackages] = useState([]);
  const [hasDiscountPackage, setHasDiscountPackage] = useState(false);
  const [bestPackageSuggestion, setBestPackageSuggestion] = useState(null);
  const { t, i18n } = useTranslation();
  const { language } = i18n;
  const { usingPackage } = useSelector((state) => state.user);
  const { user } = useSelector((state) => state.auth);

  const newPricingOverviewEnabled = getFeatureValue(
    FEATURE_KEYS.NEW_PRICING_OVERVIEW,
    { ip },
  );

  const handleSetCurrentPackages = (duration, allPackages) => {
    const displayPackages = filterPackagesV3({
      duration,
      allPackages,
      packageType,
    });

    // Sort packages free put to the end of list
    setCurrentPackages(
      displayPackages?.sort((a, b) =>
        a.rank === 1 ? 1 : b.rank === 1 ? -1 : a.rank - b.rank,
      ),
    );
    const isHasDiscountPackage = checkHasDiscountPackage(displayPackages);
    setHasDiscountPackage(isHasDiscountPackage);
  };

  const listBestPackages = useMemo(
    () =>
      suggestPackages({
        packages: packagesData,
        vouchers: allVouchers,
        fromLandingPage: false,
        requirements: {
          contentsNumber: valueSlider.numberOfContentsProduced,
          textLength: valueSlider.yourScriptWordCount,
          hasForeignVoice: options.useForeignVoice,
          hasVoiceClone: options.useVoiceClone,
          totalCycles: packageDuration === DURATION.YEARLY ? 12 : 1,
        },
        currencyUnit: isVietNam ? CURRENCY_UNIT.VND : CURRENCY_UNIT.USD,
      }),
    [packagesData, allVouchers, valueSlider, options, packageDuration],
  );

  useEffect(() => {
    const bestPackage = listBestPackages?.[0];
    setBestPackageSuggestion(bestPackage);
  }, [listBestPackages]);

  const handleDiscountEnd = () => {
    const isHasDiscountPackage = checkHasDiscountPackage(currentPackages);
    setHasDiscountPackage(isHasDiscountPackage);
  };

  const handleChangePackageDuration = (value) => {
    if (useVoiceClone && value === DURATION.MONTHLY) {
      return;
    }
    setPackages(packagesDraft);
    setPackageDuration(value);
  };

  useEffect(() => {
    handleChangePackageDuration(
      bestPackageSuggestion?.expiresIn === PACK_DURATION.MONTH
        ? DURATION.MONTHLY
        : DURATION.YEARLY,
    );
  }, [bestPackageSuggestion?.code]);

  // Lấy % giảm giá tối đa theo năm
  const getCorrespondMonthPkg = ({ packageName, level, expiresIn }) => {
    if (expiresIn === PACKAGE_EXPIRED.MONTHLY) {
      return packages.find(
        (pkg) =>
          pkg.name[language] === packageName &&
          pkg.level === level &&
          pkg?.expiresIn === PACK_DURATION.MONTH,
      );
    }

    return packages.find(
      (pkg) =>
        pkg.name[language] === packageName &&
        pkg.level === level &&
        pkg?.expiresIn !== expiresIn &&
        pkg?.expiresIn === PACK_DURATION.MONTH,
    );
  };

  // Get best voucher type price or percent and package price
  const getBestVoucherAndPackagePrice = (pkg) => {
    const { startAt, endAt } = pkg.discountConfig || {};
    const isDiscountPackage = startAt && moment().isBetween(startAt, endAt);
    const { price, usdPrice } = getPackageCurrentPrice(pkg, true);
    const originalPrice = isVietNam ? price : usdPrice;
    const vouchers = getOptimalVouchersForPackage({
      pkg,
      vouchers: totalVoucher,
    });
    const [bestVoucher] = vouchers || [];

    const isBestVoucherPriceOrPercent = checkVoucherPriceOrPercent(
      bestPackageSuggestion.bestVoucher || bestVoucher,
    );
    const bestVoucherPriceOrPercent = isBestVoucherPriceOrPercent
      ? bestPackageSuggestion.bestVoucher || bestVoucher
      : null;

    return {
      totalPrice: getTotalPricePackage(
        pkg,
        isDiscountPackage,
        bestVoucherPriceOrPercent,
        isVietNam,
        true,
      ),
      bestVoucher: bestVoucherPriceOrPercent,
      isDiscountPackage,
      originalPrice,
    };
  };

  const saveUpToPercent = () => {
    const packagesYear = packages?.filter(
      (pkg) =>
        pkg?.level === bestPackageSuggestion?.level &&
        pkg?.expiresIn === PACK_DURATION.YEAR,
    );

    const percents = packagesYear?.map((pkg) => {
      const totalPrice = isVietNam ? pkg?.price : pkg?.usdPrice;

      const correspondMonthPkg = getCorrespondMonthPkg({
        packageName: pkg?.name?.[language],
        level: pkg.level,
        expiresIn: pkg.expiresIn,
      });
      const correspondMonthPrice = isVietNam
        ? correspondMonthPkg?.price
        : correspondMonthPkg?.usdPrice;

      if (!correspondMonthPrice || !totalPrice) return 0;

      const monthByDuration = Math.round(pkg?.expiresIn / 30);
      const saleOffPercentage =
        (1 - totalPrice / (correspondMonthPrice * monthByDuration)) * 100;
      return saleOffPercentage;
    });

    return roundSaleOffPercentage(
      percents.length > 0 ? Math.max(...percents) : 0,
    );
  };

  const checkExpiredDate = (expiryDate) => {
    if (!expiryDate) return false;
    return moment().isAfter(expiryDate);
  };

  const getUsingPackage = (pkgType) => {
    switch (pkgType) {
      case PACKAGE_TYPE.STUDIO: {
        const studioPackage = packages.find(
          (pkg) => pkg.code === user?.packageCode,
        );
        return {
          ...studioPackage,
          isExpires: checkExpiredDate(user?.packageExpiryDate),
        };
      }
      case PACKAGE_TYPE.API: {
        const apiPackage = packages.find(
          (pkg) => pkg.code === user?.apiPackage?.packageCode,
        );
        return {
          ...apiPackage,
          isExpires: checkExpiredDate(user?.apiPackage?.packageExpiryDate),
        };
      }
      default:
        return null;
    }
  };

  const getPrice = (pkg) => {
    // Calculate sale off percentage
    const { totalPrice, bestVoucher, isDiscountPackage, originalPrice } =
      getBestVoucherAndPackagePrice(pkg);

    if (!originalPrice || !totalPrice) return 0;

    const saleOffPercentage = (1 - totalPrice / originalPrice) * 100;
    const saleOffPercentageRounded = roundSaleOffPercentage(saleOffPercentage);

    // Calculate total price and monthly price with sale off
    const monthlyPriceWithSaleOff = calculateSaleOffMonthlyPrice(
      pkg,
      isDiscountPackage,
      bestVoucher,
      isVietNam,
      packageDuration,
    );

    return {
      totalPrice,
      monthlyPriceWithSaleOff,
      saleOffPercentageRounded,
    };
  };

  const bestPackageSameLevel = useMemo(
    () =>
      listBestPackages?.find(
        (pkg) =>
          pkg.level === bestPackageSuggestion?.level &&
          pkg.expiresIn !== bestPackageSuggestion?.expiresIn,
      ),
    [listBestPackages, bestPackageSuggestion],
  );

  useEffect(() => {
    setTotalVoucher(allVouchers);
  }, [allVouchers]);

  useEffect(() => {
    setPackages(packagesData);
    setPackagesDraft(packagesData);
  }, [packagesData?.length]);

  useEffect(() => {
    handleSetCurrentPackages(packageDuration, packages);
  }, [usingPackage, packageDuration, packages]);

  const renderPackageItem = () => {
    const currentDuration =
      packageDuration === DURATION.MONTHLY
        ? PACK_DURATION.MONTH
        : PACK_DURATION.YEAR;

    const packageSuggestion =
      bestPackageSuggestion &&
      currentDuration === bestPackageSuggestion?.expiresIn
        ? bestPackageSuggestion
        : bestPackageSameLevel;

    const pkg = packages?.find(
      (item) => item?.code === packageSuggestion?.code,
    );

    const packageItem = pkg
      ? { ...pkg, creditsPerCycle: packageSuggestion?.creditsPerCycle }
      : null;

    if (!packageItem) return null;

    const correspondMonthPkg = getCorrespondMonthPkg({
      packageName: packageItem?.name?.[language],
      level: packageItem.level,
      expiresIn: packageItem.expiresIn,
    });
    const correspondMonthPrice = isVietNam
      ? correspondMonthPkg?.price
      : correspondMonthPkg?.usdPrice;

    const { totalPrice, monthlyPriceWithSaleOff, saleOffPercentageRounded } =
      getPrice(pkg);

    const packageTopUp =
      packages.find((item) => item.code === pkg.topUpPackageCode) || {};

    return (
      <>
        {newPricingOverviewEnabled ? (
          <NewBestPackageItem
            packages={packages}
            saleOffPercentage={saleOffPercentageRounded}
            monthlyPriceWithSaleOff={monthlyPriceWithSaleOff || 0}
            correspondMonthPrice={correspondMonthPrice}
            totalPrice={totalPrice}
            pkg={packageItem}
            isMobile={isMobile}
            duration={packageDuration}
            hasDiscountPackage={hasDiscountPackage}
            onDiscountEnd={handleDiscountEnd}
            canUseVouchers={getOptimalVouchersForPackage({
              pkg: packageItem,
              vouchers: totalVoucher,
            })}
            currentUsingPackage={getUsingPackage(packageItem?.type)}
            packageTopUp={packageTopUp}
            setPackages={setPackages}
            correspondMonthPkg={correspondMonthPkg}
            bestPackageSuggestion={bestPackageSuggestion}
            setBestPackageSuggestion={setBestPackageSuggestion}
          />
        ) : (
          <BestPackItem
            packages={packages}
            saleOffPercentage={saleOffPercentageRounded}
            monthlyPriceWithSaleOff={monthlyPriceWithSaleOff || 0}
            correspondMonthPrice={correspondMonthPrice}
            totalPrice={totalPrice}
            pkg={packageItem}
            isMobile={isMobile}
            duration={packageDuration}
            hasDiscountPackage={hasDiscountPackage}
            onDiscountEnd={handleDiscountEnd}
            canUseVouchers={getOptimalVouchersForPackage({
              pkg: packageItem,
              vouchers: totalVoucher,
            })}
            currentUsingPackage={getUsingPackage(packageItem?.type)}
            packageTopUp={packageTopUp}
            setPackages={setPackages}
            correspondMonthPkg={correspondMonthPkg}
            bestPackageSuggestion={bestPackageSuggestion}
            setBestPackageSuggestion={setBestPackageSuggestion}
          />
        )}
      </>
    );
  };

  return (
    <StyledPackage disabledTab={!bestPackageSameLevel}>
      <>
        <Typography
          style={{
            display: 'flex',
            justifyContent: 'center',
          }}
          variant="body-16-semibold"
          color={COLOR.darkGray}
        >
          {t('packageSuggestionTitle')}
        </Typography>
        <TabDuration
          percentage={saveUpToPercent()}
          packageDuration={packageDuration}
          handleChangePackageDuration={handleChangePackageDuration}
        />

        {renderPackageItem()}
      </>
    </StyledPackage>
  );
};

export default BestPackageSuggestion;
